import React, { useCallback, useState, useEffect, useRef } from "react";
import { getFolder } from "redux/actions";

export const useDynamicFolderHeights = (
  folders: getFolder[],
  accordionState?: Record<number, boolean>
) => {
  const [folderHeights, setFolderHeights] = useState<Record<number, number>>({});
  const folderContainerRef = useRef<HTMLDivElement>(null);
  const folderRefs = useRef<{ [key: number]: React.RefObject<HTMLDivElement> }>({});

  // Initialize folder refs for each folder
  useEffect(() => {
    folders.forEach(folder => {
      if (!folderRefs.current[folder.id]) {
        folderRefs.current[folder.id] = React.createRef<HTMLDivElement>();
      }
    });
  }, [folders]);

  const calculateLastFolderHeight = useCallback(() => {
    if (!folderContainerRef.current || !folders?.length) return {};

    // const containerRect = folderContainerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Get the last folder
    const lastFolder = folders[folders.length - 1];
    if (!lastFolder) return {};
    const DATE_HEADER_HEIGHT = 80; // Height of date group headers
    const CHAT_ITEM_HEIGHT = 56; // Average height of each chat item
    const BOTTOM_SPACING = 326; // Space for footer and other UI elements at bottom
    
    const maxAvailableHeight = windowHeight - BOTTOM_SPACING;
    
    // Initialize result object
    const heights: Record<number, number> = {};
    
    // Calculate height only for the last folder
    const isLastFolderOpen = accordionState?.[lastFolder.id] !== false;
    
    if (isLastFolderOpen) {
      const chatCount = lastFolder.chats.length;
      
      if (chatCount === 0) {
        // Minimum height for empty folders
        heights[lastFolder.id] = 100;
      } else {
        // Estimate number of date headers (assuming approximately one header per 5 chats, minimum 1)
        const estimatedDateHeaders = Math.max(1, Math.ceil(chatCount / 5));
        const dateHeadersHeight = estimatedDateHeaders * DATE_HEADER_HEIGHT;
        
        // Calculate actual content height needed
        const contentHeight = (chatCount * CHAT_ITEM_HEIGHT) + dateHeadersHeight;
        
        // Use the smaller of content height or max available height
        heights[lastFolder.id] = Math.min(contentHeight, maxAvailableHeight);
        
        // Ensure minimum reasonable height
        heights[lastFolder.id] = Math.max(heights[lastFolder.id], 200);
      }
    } else {
      heights[lastFolder.id] = 0;
    }
    
    return heights;
  }, [folders, accordionState]);

  // Calculate heights on initial render and when dependencies change
  useEffect(() => {
    // Initial calculation
    const newHeights = calculateLastFolderHeight();
    setFolderHeights(newHeights);

    // Recalculate on window resize
    const handleResize = () => {
      const updatedHeights = calculateLastFolderHeight();
      setFolderHeights(updatedHeights);
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateLastFolderHeight]);

  return { 
    folderHeights, 
    folderContainerRef, 
    folderRefs 
  };
};
